import { CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

interface Props {
  readonly title: string;
  readonly description?: string;
  readonly spotlight?: boolean;
  readonly working?: boolean;
  readonly visible?: boolean;
}

export const Section: React.FC<React.PropsWithChildren<Props>> = (
  props: React.PropsWithChildren<Props>,
): React.ReactElement => {
  const {
    title,
    description,
    spotlight = false,
    visible = false,
    working = false,
    children,
  } = props;
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect((): void => {
    const { current: element } = ref;
    if (visible && spotlight && element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, [spotlight, visible]);

  if (!visible) {
    return <></>;
  }

  return (
    <Paper ref={ref} elevation={3} sx={styles.section}>
      <Box mb={3}>
        <Box position="relative" zIndex={1}>
          <Typography variant="h7" color="primary.main">
            {title}
          </Typography>
          {working && (
            <Box sx={styles.spinner}>
              <CircularProgress size={16} thickness={8} />
            </Box>
          )}
        </Box>
        {description ? (
          <>
            <Box height={12} />
            <Typography fontSize={16} fontWeight={400} lineHeight="24px" color="custom.LightGray">
              {description}
            </Typography>
          </>
        ) : null}
      </Box>
      {children}

      {working && <Box sx={styles.overlay} />}
    </Paper>
  );
};

const styles: Record<string, SystemStyleObject> = {
  section: {
    position: 'relative',
    width: 552,
    mx: 'auto',
    padding: 4,
  },
  spinner: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    top: 0,
    right: 0,
    borderRadius: 'inherit',
    width: 24,
    height: 24,
  },
  overlay: {
    position: 'absolute',
    borderRadius: 'inherit',
    backgroundColor: 'background.paper',
    backdropFilter: 'grayscale(80%)',
    opacity: 0.75,
    inset: 0,
    zIndex: 1,
  },
};
